import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  School,
  Schedule,
  PlayCircleOutline,
} from '@mui/icons-material';
import { trainingService, TrainingCourse } from '../services/trainingService';
import TrainingVideoPlayer from '../components/TrainingVideoPlayer';
import TrainingQuiz from '../components/TrainingQuiz';
import CourseCompletionModal from '../components/CourseCompletionModal';
import ModuleAcknowledgmentGate from '../components/ModuleAcknowledgmentGate';

export default function TrainingCoursePage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState<TrainingCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  // FEAT-046 — module Acknowledgment Gate appears once the video reports
  // 100% watch progress. The gate blocks the quiz / completion modal
  // (MVP-18.20) until the caregiver attests they understood the material.
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [moduleAcknowledged, setModuleAcknowledged] = useState(false);

  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const loadCourse = async () => {
    if (!courseId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await trainingService.getCourse(courseId);
      setCourse(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!courseId) return;
    
    setEnrolling(true);
    setError(null);
    try {
      await trainingService.enrollInCourse(courseId);
      // Reload course to get updated progress
      await loadCourse();
    } catch (err: any) {
      setError(err.message || 'Failed to enroll in course');
    } finally {
      setEnrolling(false);
    }
  };

  const handleVideoComplete = () => {
    setVideoCompleted(true);
  };

  const handleAcknowledged = () => {
    setModuleAcknowledged(true);
    if (course?.quizQuestionCount && course.quizQuestionCount > 0) {
      setShowQuiz(true);
    } else {
      setShowCompletionModal(true);
    }
  };

  const handleQuizPass = () => {
    setShowQuiz(false);
    setShowCompletionModal(true);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !course) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Course not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/training')} sx={{ mt: 2 }}>
          Back to Training
        </Button>
      </Container>
    );
  }

  const progress = course.userProgress;
  const isEnrolled = !!progress;
  const isCompleted = progress?.status === 'COMPLETED';
  const progressPercentage = progress
    ? Math.round((progress.videoProgress / progress.videoDuration) * 100) || 0
    : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/training')}
        sx={{ mb: 3 }}
      >
        Back to Training
      </Button>

      {/* Course Header */}
      <Paper sx={{ p: 4, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box flex={1}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              {course.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {course.description}
            </Typography>
          </Box>
        </Box>

        {/* Course Meta */}
        <Box display="flex" gap={1} flexWrap="wrap" mb={3}>
          <Chip icon={<School />} label={course.difficultyLevel} />
          <Chip icon={<Schedule />} label={`${course.estimatedDuration} minutes`} />
          {course.category && <Chip label={course.category} variant="outlined" />}
          {course.isRequired && <Chip label="Required" color="error" />}
          {course.isRecommended && <Chip label="Recommended" color="warning" />}
          {isCompleted && (
            <Chip icon={<CheckCircle />} label="Completed" color="success" />
          )}
        </Box>

        {/* Progress Bar */}
        {isEnrolled && !isCompleted && (
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Your Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {progressPercentage}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progressPercentage} />
          </Box>
        )}

        {/* Learning Objectives */}
        {course.learningObjectives.length > 0 && (
          <Box mb={2}>
            <Typography variant="h6" gutterBottom>
              What You'll Learn
            </Typography>
            <List dense>
              {course.learningObjectives.map((objective, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckCircle color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={objective} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Enroll Button */}
        {!isEnrolled && (
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayCircleOutline />}
            onClick={handleEnroll}
            disabled={enrolling}
            fullWidth
          >
            {enrolling ? 'Enrolling...' : 'Start Course'}
          </Button>
        )}
      </Paper>

      {/* Video Player */}
      {isEnrolled && !showQuiz && (
        <Paper sx={{ mb: 3 }}>
          <TrainingVideoPlayer
            courseId={course.id}
            videoUrl={course.videoUrl || ''}
            captionUrl={course.captionUrl}
            transcriptText={course.transcriptText}
            currentPosition={progress?.currentPosition || 0}
            onComplete={handleVideoComplete}
          />
        </Paper>
      )}

      {/* FEAT-046 — per-module Acknowledgment Gate (MVP-18.20).
          Appears after the video reports 100% watch progress. Blocks the
          quiz / completion modal until the caregiver attests they understood
          the module. The gate is idempotent on retry. */}
      {isEnrolled && !isCompleted && videoCompleted && !showQuiz && (
        <Box sx={{ mb: 3 }}>
          <ModuleAcknowledgmentGate
            moduleId={course.id}
            initiallyAcknowledged={moduleAcknowledged}
            onAcknowledged={handleAcknowledged}
          />
        </Box>
      )}

      {/* Quiz */}
      {showQuiz && courseId && (
        <Paper sx={{ p: 4 }}>
          <TrainingQuiz
            courseId={courseId}
            passingScore={course.passingScore}
            onPass={handleQuizPass}
            onCancel={() => setShowQuiz(false)}
          />
        </Paper>
      )}

      {/* Completion Modal */}
      {showCompletionModal && courseId && (
        <CourseCompletionModal
          open={showCompletionModal}
          courseId={courseId}
          courseName={course.title}
          onClose={() => {
            setShowCompletionModal(false);
            navigate('/training/certificates');
          }}
        />
      )}
    </Container>
  );
}

